import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlay, FiDollarSign, FiPercent, FiUserPlus, FiMousePointer, FiEye } = FiIcons;

const YouTubeAds = ({ client, data }) => {
  const safeData = data || {
    viewRate: 0, costPerView: 0, completion: 0, earnedSubs: 0, ctr: 0, views: 0,
    history: [], campaigns: []
  };

  const historyData = safeData.history && safeData.history.length > 0 ? safeData.history : [{ month: 'No Data', viewRate: 0, completion: 0 }];

  const metrics = [
    { title: 'View Rate', value: `${safeData.viewRate}%`, icon: FiPlay, color: 'text-red-600' },
    { title: 'Cost/View', value: `$${safeData.costPerView}`, icon: FiDollarSign, color: 'text-green-600' },
    { title: 'Completion', value: `${safeData.completion}%`, icon: FiPercent, color: 'text-blue-600' },
    { title: 'Earned Subs', value: safeData.earnedSubs, icon: FiUserPlus, color: 'text-purple-600' },
    { title: 'Total Views', value: safeData.views.toLocaleString(), icon: FiEye, color: 'text-indigo-600' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">YouTube Advertising</h2>
        <p className="text-gray-600">Video advertising performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg bg-opacity-10 flex items-center justify-center ${metric.color.replace('text-', 'bg-')}`}>
                <SafeIcon icon={metric.icon} className={`text-lg ${metric.color}`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{metric.value}</h3>
            <p className="text-sm text-gray-600">{metric.title}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">View Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={historyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Line type="monotone" dataKey="viewRate" stroke="#FF0000" strokeWidth={3} name="View Rate (%)" />
            <Line type="monotone" dataKey="completion" stroke="#00A859" strokeWidth={3} name="Completion (%)" />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>
        
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Campaigns</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Campaign</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Views</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Completion</th>
              </tr>
            </thead>
            <tbody>
              {safeData.campaigns && safeData.campaigns.map((camp, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-800">{camp.name}</td>
                  <td className="py-4 px-4 text-right text-gray-600">{camp.views.toLocaleString()}</td>
                  <td className="py-4 px-4 text-right text-gray-600">{camp.completion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default YouTubeAds;